import { AxiosError } from "axios";
import { Dispatch } from "redux";
import { AppState } from "..";

import { organizationEventParticipationsService } from "../../api";
import {
  CLEAR_ORGANIZATION_EVENT_PARTICIPATIONS_DATA,
  ClearOrganizationEventParticipationsDataAction,
  ORGANIZATION_EVENT_PARTICIPATIONS_FAILURE,
  ORGANIZATION_EVENT_PARTICIPATIONS_REQUEST,
  OrganizationEventParticipationsActionTypes,
  OrganizationEventParticipationsFailureAction,
  OrganizationEventParticipationsRequestAction,
  SET_ORGANIZATION_EVENT_PARTICIPATIONS_EVENT_SORTED_DATA,
  SET_ORGANIZATION_EVENT_PARTICIPATIONS_USER_SORTED_DATA,
  SetOrganizationEventParticipationsEventSortedDataAction,
  SetOrganizationEventParticipationsUserSortedDataAction,
} from "./types";
import {
  OrganizationEventParticipationsData,
  OrganizationEventParticipationsResponse,
} from "../../models/organizationEventParticipationsData";

/**
 * Fetches event participations for a specific organization
 * @param organizationId - The ID of the organization
 * @param sortedByEvent - True if data must be sorted by event
 * @returns {Function} A function that dispatches actions
 */
export function getOrganizationEventParticipationsByOrganizationId(
  organizationId: string,
  sortedByEvent: boolean
) {
  return (
    dispatch: Dispatch<OrganizationEventParticipationsActionTypes>,
    /* eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars */
    getState: () => AppState
  ) => {
    dispatch(organizationEventParticipationRequest());
    return (
      organizationEventParticipationsService
        .getEventParticipationsByOrganizationId(organizationId, sortedByEvent)
        .then((eventParticipations: OrganizationEventParticipationsResponse) => {
          const { event_participations, total_progress_percentage } =
            eventParticipations;
          if (sortedByEvent) {
            dispatch(
              setOrganizationEventParticipationsEventSortedData(
                event_participations,
                total_progress_percentage
              )
            );
          } else {
            dispatch(
              setOrganizationEventParticipationsUserSortedData(
                event_participations,
                total_progress_percentage
              )
            );
          }
        })
        /* eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars */
        .catch((err: AxiosError) => {
          dispatch(organizationEventParticipationFailure());
        })
    );
  };
}

/**
 * Dispatched when the organization event participations request is made
 * @returns {OrganizationEventParticipationsRequestAction}
 */
export function organizationEventParticipationRequest(): OrganizationEventParticipationsRequestAction {
  return {
    type: ORGANIZATION_EVENT_PARTICIPATIONS_REQUEST,
  };
}

/**
 * Dispatched when the organization event participations request fails
 * @returns {OrganizationEventParticipationsFailureAction}
 */
export function organizationEventParticipationFailure(): OrganizationEventParticipationsFailureAction {
  return {
    type: ORGANIZATION_EVENT_PARTICIPATIONS_FAILURE,
  };
}

/**
 * Sets the organization event participations data sorted by event
 * @param {OrganizationEventParticipationsData} eventSortedData - The organization event participiations sorted by event
 */
export function setOrganizationEventParticipationsEventSortedData(
  eventSortedData: OrganizationEventParticipationsData[],
  totalProgressPercentage: number
): SetOrganizationEventParticipationsEventSortedDataAction {
  return {
    type: SET_ORGANIZATION_EVENT_PARTICIPATIONS_EVENT_SORTED_DATA,
    eventSortedData,
    totalProgressPercentage,
  };
}

/**
 * Sets the organization event participations data sorted by user
 * @param {OrganizationEventParticipationsData} userSortedData - The organization event participiations sorted by user
 */
export function setOrganizationEventParticipationsUserSortedData(
  userSortedData: OrganizationEventParticipationsData[],
  totalProgressPercentage: number
): SetOrganizationEventParticipationsUserSortedDataAction {
  return {
    type: SET_ORGANIZATION_EVENT_PARTICIPATIONS_USER_SORTED_DATA,
    userSortedData,
    totalProgressPercentage,
  };
}

/**
 * Clears the organization event participations data
 * @returns {OrganizationEventParticipationsRequestAction}
 */
export function clearOrganizationEventParticipationsData(): ClearOrganizationEventParticipationsDataAction {
  return {
    type: CLEAR_ORGANIZATION_EVENT_PARTICIPATIONS_DATA,
  };
}
